import { forwardRef } from 'react';
import styled from 'styled-components';
import { spacing } from 'ui/theme';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Goals16 } from 'ui/icons/aurora/16px/studio/Goals16';
import { AddNew24 } from 'ui/icons/aurora/24px/AddNew24';
import noop from 'utils/noop';
import { SelectValueDisplayProps, SelectValueType } from 'ui/selects';
import { ErrorBoundary } from 'modules/error/ErrorBoundary';
import { MissionControlModule } from 'modules/missionControl/constants';
import { Team, Product } from 'teams';
import {
  DashboardSection,
  DashboardSectionHeader,
  DashboardSectionTitle,
} from '../Dashboard.styled';
import { GoalsDashboardContents } from './GoalsDashboardContents';
import { useGoalsDashboardState } from './hooks/useGoalsDashboardState';
import { AddGoalSelect } from './AddGoalSelect';
import { HomepageErrorBoundaryState } from '../HomepageErrorBoundaryState';

const StyledDashboardSection = styled(DashboardSection)`
  gap: ${spacing.threeQuarter};
`;

const StyledDashboardSectionHeader = styled(DashboardSectionHeader)`
  justify-content: space-between;
  width: 100%;
`;

const StyledButton = styled(Button)`
  flex-shrink: 0;
`;

export const GoalsDashboard = () => {
  const { isLoadingState, isEmptyState, isErrorState } = useGoalsDashboardState();

  const Trigger = forwardRef<HTMLButtonElement, SelectValueDisplayProps<SelectValueType>>(
    ({ toggleDropdown }, ref) => (
      <StyledButton
        ref={ref}
        size={BUTTON_SIZES.SMALL}
        variant={BUTTON_VARIANTS.GHOST}
        onClick={() => toggleDropdown(true)}
      >
        <AddNew24 width={16} />
        Add goal
      </StyledButton>
    ),
  );

  return (
    <StyledDashboardSection data-test="goals-dashboard">
      <ErrorBoundary
        teamName={Team.NOVA_TEAM}
        productName={Product.HOMEPAGE}
        errorContext={{
          module: MissionControlModule.GOALS_DASHBOARD,
        }}
        errorMarkup={
          <>
            {/* NOTE: duplicated the header here instead of reusing the one with AddGoalSelect to ensure errors from AddGoalSelect are also caught */}
            <StyledDashboardSectionHeader>
              <DashboardSectionTitle>
                <Goals16 /> Goals
              </DashboardSectionTitle>
            </StyledDashboardSectionHeader>

            <HomepageErrorBoundaryState data-test="goals-dashboard-error-boundary-state" />
          </>
        }
      >
        <StyledDashboardSectionHeader>
          <DashboardSectionTitle>
            <Goals16 /> Goals
          </DashboardSectionTitle>

          {!isLoadingState && !isEmptyState && !isErrorState && (
            <AddGoalSelect
              onSelect={noop} // Not used. The options are never selectable but instead provide various actions. (PS. this not the right component for this behavior, but it supports the correct styles)
              Trigger={Trigger}
            />
          )}
        </StyledDashboardSectionHeader>

        <GoalsDashboardContents />
      </ErrorBoundary>
    </StyledDashboardSection>
  );
};
